import { useEffect, useState } from "react";
import { useGetAccount } from "@multiversx/sdk-dapp/out/react/account/useGetAccount";
import { useGetIsLoggedIn } from "@multiversx/sdk-dapp/out/react/account/useGetIsLoggedIn";
import { type AppConfig } from "./config";
import { disconnect as walletDisconnect } from "./auth";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import HatomNfts from "./pages/HatomNfts";
import Navbar from "./Navbar";
import WalletPicker from "./WalletPicker";
import ConsentBanner from "./ConsentBanner";

// Static imports — React.lazy here would split escrow.ts into its own chunk
// and break vite-plugin-node-polyfills CJS init order (was tried, reverted).

type Props = {
  initialConfig: AppConfig | null;
  initialError: string | null;
  contractOwner: string;
};

type Tab = "staked" | "hatom";

export default function App({ initialConfig, initialError, contractOwner }: Props) {
  const [cfg] = useState<AppConfig | null>(initialConfig);
  const [err] = useState<string | null>(initialError);
  // Default to HATOM NFTs when the new contract is configured, else fall
  // back to the legacy Staked Tokens surface.
  const [tab, setTab] = useState<Tab>(
    initialConfig?.nftLiquidityContractAddress ? "hatom" : "staked",
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  const isLoggedIn = useGetIsLoggedIn();
  const account = useGetAccount();

  useEffect(() => {
    if (cfg) document.title = `Escrow — ${cfg.network}`;
  }, [cfg]);

  if (err || !cfg) {
    return (
      <div className="main">
        <div className="card">
          <h3>Configuration error</h3>
          <p className="muted mono">{err ?? "config missing"}</p>
          <p className="muted">
            The dApp expects a Node server on <code className="mono">/api/config</code>{" "}
            returning <code className="mono">{`{ network, contractAddress }`}</code>.
            Everything else (owner, pairs, tokens) is queried from the contract.
          </p>
        </div>
      </div>
    );
  }

  const loggedInAddress = isLoggedIn ? account?.address ?? null : null;
  const isOwner =
    !!contractOwner &&
    !!loggedInAddress &&
    loggedInAddress.toLowerCase() === contractOwner.toLowerCase();
  const nftAvailable = !!cfg.nftLiquidityContractAddress;

  function onDisconnect() {
    walletDisconnect().then(() => location.reload());
  }

  return (
    <>
      {/* always-on-top navbar */}
      <Navbar
        network={cfg.network}
        loggedInAddress={loggedInAddress}
        isOwner={isOwner}
        onConnect={() => setPickerOpen(true)}
        onDisconnect={onDisconnect}
      />

      {/* tabs only when logged in AND the NFT contract is configured */}
      {isLoggedIn && nftAvailable && (
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            padding: "0.6rem 1.4rem 0",
            borderBottom: "1px solid var(--border)",
            maxWidth: 1100,
            margin: "0 auto",
            background: "var(--bg-elev)",
          }}
        >
          <TabButton active={tab === "hatom"} onClick={() => setTab("hatom")}>
            HATOM NFTs
          </TabButton>
          <TabButton active={tab === "staked"} onClick={() => setTab("staked")}>
            Staked Tokens
          </TabButton>
        </div>
      )}

      {/* main content */}
      {!isLoggedIn ? (
        <Landing config={cfg} contractOwner={contractOwner} />
      ) : tab === "hatom" && nftAvailable ? (
        <HatomNfts config={cfg} address={account.address} isOwner={isOwner} />
      ) : (
        <Dashboard
          config={cfg}
          address={account.address}
          isOwner={isOwner}
          contractOwner={contractOwner}
        />
      )}

      {/* wallet picker (top-level so it works from any screen) */}
      {pickerOpen && (
        <WalletPicker
          onClose={() => setPickerOpen(false)}
          walletConnectEnabled={!!cfg.walletConnectProjectId}
        />
      )}

      <ConsentBanner />
    </>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="ghost"
      style={{
        borderRadius: 0,
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
        borderBottom: active ? "3px solid var(--accent)" : "3px solid transparent",
        background: "transparent",
        padding: "0.5rem 1rem",
        fontWeight: active ? 600 : 400,
        color: active ? "var(--text)" : "var(--text-dim)",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
